if (!customElements.get('legemeaux-sticky-purchase')) {
  customElements.define(
    'legemeaux-sticky-purchase',
    class LegemeauxStickyPurchase extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.sectionId = this.dataset.sectionId;
        if (!this.productInfo || !this.sectionId) return;

        document.body.classList.add('lgm-sticky-purchase-active');
        this.observePageOverlays();
        this.observeCanonicalPurchaseState();
        this.observeCanonicalPurchaseVisibility();
        this.syncFromCanonicalPurchaseState();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.optionSelectionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            if (!this.productInfo.contains(event?.data?.event?.target)) return;
            this.syncSelectedOptions();
            this.syncFromCanonicalPurchaseState();
          });

          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            const source = event.data.html?.querySelector(`#LegemeauxStickyPurchase-${CSS.escape(this.sectionId)}`);
            if (source) this.syncFromRenderedSource(source);
            this.syncFromCanonicalPurchaseState();
            this.observeCanonicalPurchaseVisibility();
          });
        }
      }

      disconnectedCallback() {
        this.pageOverlayObserver?.disconnect();
        this.cartDrawerOverlayObserver?.disconnect();
        this.purchaseStateObserver?.disconnect();
        this.purchaseVisibilityObserver?.disconnect();
        if (this.viewportVisibilityHandler) {
          window.removeEventListener('scroll', this.viewportVisibilityHandler);
          window.removeEventListener('resize', this.viewportVisibilityHandler);
        }
        if (this.purchaseVisibilityFrame) cancelAnimationFrame(this.purchaseVisibilityFrame);
        this.viewportVisibilityHandler = null;
        this.purchaseVisibilityFrame = null;
        this.purchaseVisibilityTarget = null;
        this.optionSelectionUnsubscriber?.();
        this.variantChangeUnsubscriber?.();

        queueMicrotask(() => {
          if (!document.querySelector('legemeaux-sticky-purchase')) {
            document.body.classList.remove('lgm-sticky-purchase-active');
            document.body.classList.remove('lgm-header-overlay-open');
          }
        });
      }

      observePageOverlays() {
        this.syncPageOverlayState();
        this.pageOverlayObserver = new MutationObserver(() => this.syncPageOverlayState());
        this.pageOverlayObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['hidden', 'open'],
          subtree: true,
        });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          this.cartDrawerOverlayObserver = new MutationObserver(() => this.syncPageOverlayState());
          this.cartDrawerOverlayObserver.observe(cartDrawer, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }

      syncPageOverlayState() {
        const pageOverlayIsOpen = Boolean(
          document.querySelector(
            'header-drawer > details[open], details-modal.header__search > details[open], .country-selector:not([hidden]), .language-selector:not([hidden]), cart-drawer.active, product-modal[open], quick-add-modal[open], pickup-availability-drawer[open], modal-dialog[open]'
          )
        );

        this.toggleAttribute('data-overlay-suppressed', pageOverlayIsOpen);
        document.body.classList.toggle('lgm-header-overlay-open', pageOverlayIsOpen);
        this.syncVisibilityAccessibility();
      }

      observeCanonicalPurchaseVisibility() {
        this.purchaseVisibilityObserver?.disconnect();
        const submitButton = this.canonicalSubmitButton;
        this.purchaseVisibilityTarget = submitButton;
        if (!submitButton) {
          this.removeAttribute('data-sticky-observed');
          this.removeAttribute('data-sticky-visible');
          this.syncVisibilityAccessibility();
          return;
        }

        this.setAttribute('data-sticky-observed', '');
        this.syncCanonicalPurchaseVisibility();

        if (!this.viewportVisibilityHandler) {
          this.viewportVisibilityHandler = () => this.scheduleCanonicalPurchaseVisibilitySync();
          window.addEventListener('scroll', this.viewportVisibilityHandler, { passive: true });
          window.addEventListener('resize', this.viewportVisibilityHandler);
        }

        if (!('IntersectionObserver' in window)) return;

        this.purchaseVisibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;

          this.syncCanonicalPurchaseVisibility(entry.boundingClientRect);
        });
        this.purchaseVisibilityObserver.observe(submitButton);
      }

      scheduleCanonicalPurchaseVisibilitySync() {
        if (this.purchaseVisibilityFrame) return;

        this.purchaseVisibilityFrame = requestAnimationFrame(() => {
          this.purchaseVisibilityFrame = null;
          this.syncCanonicalPurchaseVisibility();
        });
      }

      syncCanonicalPurchaseVisibility(bounds = this.purchaseVisibilityTarget?.getBoundingClientRect()) {
        if (!bounds) return;

        const hasPassedPurchaseButton = bounds.bottom < 0;
        this.toggleAttribute('data-sticky-visible', hasPassedPurchaseButton);
        this.syncVisibilityAccessibility();
      }

      syncVisibilityAccessibility() {
        const pageOverlayIsOpen = this.hasAttribute('data-overlay-suppressed');
        const observedAndHidden =
          this.hasAttribute('data-sticky-observed') && !this.hasAttribute('data-sticky-visible');
        const isHidden = pageOverlayIsOpen || observedAndHidden;

        if (observedAndHidden && this.contains(document.activeElement)) {
          const canonicalButton = this.canonicalSubmitButton;
          const canonicalBounds = canonicalButton?.getBoundingClientRect();
          const canonicalIsVisible =
            canonicalBounds && canonicalBounds.bottom > 0 && canonicalBounds.top < window.innerHeight;
          const focusTarget = canonicalIsVisible ? canonicalButton : document.getElementById('MainContent');
          focusTarget?.focus({ preventScroll: true });
        }

        this.toggleAttribute('inert', isHidden);
        if (isHidden) {
          this.setAttribute('aria-hidden', 'true');
        } else {
          this.removeAttribute('aria-hidden');
        }
      }

      observeCanonicalPurchaseState() {
        this.purchaseStateObserver = new MutationObserver(() => this.syncFromCanonicalPurchaseState());
        const submitButton = this.canonicalSubmitButton;
        const price = this.canonicalPrice;

        if (submitButton) {
          this.purchaseStateObserver.observe(submitButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'aria-haspopup'],
            childList: true,
            subtree: true,
          });
        }
        if (price) {
          this.purchaseStateObserver.observe(price, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
          });
        }
      }

      syncFromRenderedSource(source) {
        const sourceVariant = source.querySelector('[data-sticky-variant]');
        const sourcePrice = source.querySelector('[data-sticky-price]');
        const sourceButton = source.querySelector('.legemeaux-sticky-purchase__button');
        const sourceButtonLabel = source.querySelector('[data-sticky-button-label]');

        if (sourceVariant && this.variantLabel) this.variantLabel.textContent = sourceVariant.textContent.trim();
        if (sourcePrice && this.priceLabel) this.priceLabel.textContent = sourcePrice.textContent.trim();
        if (sourceButtonLabel && this.buttonLabel) this.buttonLabel.textContent = sourceButtonLabel.textContent.trim();
        if (sourceButton && this.stickyButton) this.stickyButton.disabled = sourceButton.disabled;
      }

      syncFromCanonicalPurchaseState() {
        const submitButton = this.canonicalSubmitButton;
        if (submitButton && this.stickyButton) {
          const label = submitButton.querySelector('span')?.textContent.trim();
          if (label && this.buttonLabel) this.buttonLabel.textContent = label;
          this.stickyButton.disabled = submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true';
          const ariaHasPopup = submitButton.getAttribute('aria-haspopup');
          if (ariaHasPopup) {
            this.stickyButton.setAttribute('aria-haspopup', ariaHasPopup);
          } else {
            this.stickyButton.removeAttribute('aria-haspopup');
          }
        }

        const price = this.canonicalPrice;
        if (!price || !this.priceLabel) return;
        this.priceLabel.hidden = price.classList.contains('hidden');
        const currentPrice =
          price.querySelector('.price--on-sale .price-item--sale') ||
          price.querySelector('.price__regular .price-item--regular');
        if (currentPrice) this.priceLabel.textContent = currentPrice.textContent.trim();
      }

      syncSelectedOptions() {
        if (!this.variantLabel) return;
        const variantSelectors = this.productInfo?.querySelector('variant-selects');
        if (!variantSelectors) return;

        const selectedValues = [
          ...variantSelectors.querySelectorAll("input[type='radio']:checked"),
          ...variantSelectors.querySelectorAll('select'),
        ]
          .map((control) => control.value?.trim())
          .filter(Boolean);

        if (selectedValues.length) this.variantLabel.textContent = selectedValues.join(' / ');
      }

      get canonicalSubmitButton() {
        return this.productInfo?.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);
      }

      get canonicalPrice() {
        return this.productInfo?.querySelector(`#price-${CSS.escape(this.sectionId)}`);
      }

      get stickyButton() {
        return this.querySelector('.legemeaux-sticky-purchase__button');
      }

      get buttonLabel() {
        return this.querySelector('[data-sticky-button-label]');
      }

      get variantLabel() {
        return this.querySelector('[data-sticky-variant]');
      }

      get priceLabel() {
        return this.querySelector('[data-sticky-price]');
      }
    }
  );
}
